import React, { useState, useEffect } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const cls = "w-full rounded-xl border border-[#2A1B1D]/12 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#D4AF37]";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function SettingsManager({ settings, reload }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(settings || {});
  }, [settings]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setHour = (day, key, value) => {
    const hours = (form.hours || DAYS.map((d) => ({ day: d, open: "10:00 AM", close: "8:00 PM", closed: false }))).map((h) =>
      h.day === day ? { ...h, [key]: value } : h
    );
    set("hours", hours);
  };

  const setBooking = (k, v) => set("booking", { ...(form.booking || {}), [k]: Number(v) });

  const addHoliday = () => set("holidays", [...(form.holidays || []), { date: "", reason: "" }]);
  const setHoliday = (i, key, value) => {
    const holidays = [...(form.holidays || [])];
    holidays[i] = { ...holidays[i], [key]: value };
    set("holidays", holidays);
  };
  const removeHoliday = (i) => set("holidays", (form.holidays || []).filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Settings saved — website updated");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hours = form.hours || DAYS.map((d) => ({ day: d, open: "10:00 AM", close: "8:00 PM", closed: false }));
  const booking = form.booking || {};

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl text-[#2A1B1D]">Business Settings</h1>
          <p className="text-sm text-[#5C4A4D] mt-1">Hours, holidays and contact — changes reflect on the site instantly.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="settings-save" className="inline-flex items-center gap-2 rounded-full bg-[#8E3B46] px-6 py-3 text-sm font-medium text-[#FDFBF7] hover:bg-[#722F38] transition-colors disabled:opacity-60">
          <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {/* Contact */}
      <Section title="Contact Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Business Name"><input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={cls} data-testid="set-name" /></F>
          <F label="Tagline"><input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className={cls} data-testid="set-tagline" /></F>
          <F label="Phone"><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={cls} data-testid="set-phone" /></F>
          <F label="WhatsApp (digits only)"><input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={cls} data-testid="set-whatsapp" /></F>
          <F label="Email"><input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={cls} data-testid="set-email" /></F>
          <F label="Instagram URL"><input value={form.instagram || ""} onChange={(e) => set("instagram", e.target.value)} className={cls} /></F>
          <div className="md:col-span-2"><F label="Address"><textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} className={cls} data-testid="set-address" /></F></div>
        </div>
      </Section>

      {/* Working hours */}
      <Section title="Working Hours">
        <div className="space-y-2">
          {hours.map((h) => (
            <div key={h.day} className="flex flex-wrap items-center gap-3 py-2 border-b border-[#2A1B1D]/6" data-testid={`hours-${h.day}`}>
              <span className="w-28 text-sm font-medium text-[#2A1B1D]">{h.day}</span>
              <button onClick={() => setHour(h.day, "closed", !h.closed)} data-testid={`hours-toggle-${h.day}`}
                className={`rounded-full px-3 py-1 text-xs ${h.closed ? "bg-[#8E3B46]/10 text-[#8E3B46]" : "bg-[#3E664E]/10 text-[#3E664E]"}`}>
                {h.closed ? "Closed" : "Open"}
              </button>
              {!h.closed && (
                <>
                  <input value={h.open} onChange={(e) => setHour(h.day, "open", e.target.value)} className={`${cls} w-32`} placeholder="10:00 AM" />
                  <span className="text-[#5C4A4D]">to</span>
                  <input value={h.close} onChange={(e) => setHour(h.day, "close", e.target.value)} className={`${cls} w-32`} placeholder="8:00 PM" />
                </>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-[#5C4A4D] mt-3">Use format like <span className="font-mono-x">5:00 PM</span> or <span className="font-mono-x">17:00</span>.</p>
      </Section>

      {/* Booking rules */}
      <Section title="Booking Rules">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F label="Slot length (minutes)"><input type="number" value={booking.slot_minutes ?? 60} onChange={(e) => setBooking("slot_minutes", e.target.value)} className={cls} data-testid="set-slot-minutes" /></F>
          <F label="Max bookings per slot"><input type="number" value={booking.max_per_slot ?? 1} onChange={(e) => setBooking("max_per_slot", e.target.value)} className={cls} data-testid="set-max-slot" /></F>
          <F label="Advance booking (days)"><input type="number" value={booking.advance_days ?? 30} onChange={(e) => setBooking("advance_days", e.target.value)} className={cls} /></F>
        </div>
      </Section>

      {/* Holidays */}
      <Section title="Holidays">
        <div className="space-y-3">
          {(form.holidays || []).map((hol, i) => (
            <div key={i} className="flex flex-wrap items-center gap-3" data-testid={`holiday-${i}`}>
              <input type="date" value={hol.date || ""} onChange={(e) => setHoliday(i, "date", e.target.value)} className={`${cls} w-44`} data-testid={`holiday-date-${i}`} />
              <input value={hol.reason || ""} onChange={(e) => setHoliday(i, "reason", e.target.value)} className={`${cls} flex-1 min-w-[180px]`} placeholder="Reason (e.g. Diwali)" />
              <button onClick={() => removeHoliday(i)} className="p-2 rounded-lg hover:bg-[#8E3B46]/10 text-[#8E3B46]" data-testid={`remove-holiday-${i}`}><Trash2 size={15} /></button>
            </div>
          ))}
          <button onClick={addHoliday} data-testid="add-holiday" className="inline-flex items-center gap-2 rounded-full border border-[#8E3B46] px-4 py-2 text-xs font-medium text-[#8E3B46] hover:bg-[#8E3B46] hover:text-[#FDFBF7] transition-colors">
            <Plus size={14} /> Add Holiday
          </button>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8 rounded-2xl bg-white border border-[#2A1B1D]/8 p-6 shadow-[0_8px_32px_rgba(42,27,29,0.04)]">
      <h2 className="font-display text-2xl text-[#2A1B1D] mb-5">{title}</h2>
      {children}
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><span className="text-xs font-medium text-[#5C4A4D] mb-1.5 block">{label}</span>{children}</label>;
}
